import type { Translations } from "./index";

export const es: Translations = {
  seo: {
    landing: {
      title: "Copiloto de IA en vivo para reuniones en inglés, portugués y español",
      description:
        "Minuta escucha la reunión, detecta cuando una pregunta va dirigida a ti y sugiere una respuesta clara en inglés, portugués o español basada en tus documentos y en lo que ya se dijo.",
    },
    downloads: {
      title: "Descargar Minuta para macOS y Windows",
      description:
        "Descarga la app Minuta para macOS y Windows. Captura de audio local, transcripción rápida en nube, traducción en tiempo real, acta automática y copiloto de IA dentro de la reunión.",
    },
    signup: { title: "Crear tu cuenta", description: "Crea tu cuenta gratuita de Minuta." },
    confirm: { title: "Confirmar tu correo", description: "Confirma tu correo para terminar de crear tu cuenta de Minuta." },
    done: { title: "Cuenta lista", description: "Tu cuenta de Minuta está lista." },
    login: { title: "Iniciar sesión", description: "Inicia sesión en tu cuenta de Minuta." },
    account: { title: "Mi cuenta", description: "Tu cuenta de Minuta y el plan actual." },
    billingDone: { title: "Pago completado", description: "Tu plan Pro de Minuta se está activando." },
    billingCancelled: { title: "Compra cancelada", description: "Tu compra de Minuta fue cancelada." },
    launch: {
      title: "Minuta en Product Hunt — 30% off en Pro de por vida",
      description:
        "Deja de bloquearte cuando llega la pregunta. Copiloto multilingüe de reuniones sin bot para Mac. 30% off en Pro con código HUNT30 — solo el día del Product Hunt.",
      ogImage: "/og/launch-es.png",
    },
    privacy: {
      title: "Política de Privacidad",
      description:
        "Cómo Minuta trata los datos de tu reunión: captura de audio local y procesamiento seguro en nube con política de retención limitada.",
    },
    terms: {
      title: "Términos de Servicio",
      description:
        "Términos de servicio para usar Minuta — el copiloto de IA para reuniones en macOS y Windows. Cuenta, suscripción, uso aceptable y limitación de responsabilidad.",
    },
  },
  nav: {
    copilot: "Copiloto",
    howItWorks: "Cómo funciona",
    pricing: "Precios",
    faq: "FAQ",
    blog: "Blog",
    signIn: "Iniciar sesión",
    getStarted: "Empezar gratis",
  },
  cookieConsent: {
    ariaLabel: "Consentimiento de cookies",
    kicker: "Elección de privacidad",
    title: "Cookies y privacidad",
    body:
      "Usamos almacenamiento esencial para operar el sitio y, solo con tu consentimiento, cookies o tecnologías similares opcionales para medir el producto y apoyar el checkout. Esta elección cubre LGPD y RGPD para visitantes en Brasil y Europa.",
    privacyLink: "Lee la política de privacidad",
    reject: "Rechazar",
    accept: "Aceptar",
  },
  hero: {
    badge: "Copiloto en vivo · reuniones EN/PT/ES · sin bot",
    h1Line1: "Minuta escucha la call.",
    h1Line2: "Tú sabes qué responder.",
    description:
      "En el instante en que la pregunta es para ti, Minuta detecta el idioma y arma una respuesta clara en inglés, portugués o español — basada en tus documentos y en todo lo que ya se dijo. La lees y la dices con confianza.",
    ctaPrimary: "Descargar prueba gratis",
    ctaPrimarySub: "macOS · Windows",
    ctaSecondary: "Ver el copiloto",
    ctaSecondarySub: "pregunta → respuesta",
    footnote: "7 días con acceso completo · Copiloto incluido · Sin tarjeta de crédito",
  },
  whereAudioGoes: {
    label: "Capa de confianza",
    heading: "Captura local. Cadena de IA en nube. Sin bot en la reunión.",
    description:
      "Minuta sigue siendo una app de escritorio para captura de audio del sistema, enviando el mínimo necesario por conexiones seguras en nube para transcripción, resúmenes, ID de hablantes y respuestas del copiloto.",
    privateLink: "Política de privacidad",
    noBotLink: "Setup sin bot",
    steps: [
      {
        title: "Tu computadora captura la llamada",
        body: "Zoom, Teams, Google Meet y llamadas en el navegador funcionan normalmente. Minuta escucha desde tu máquina; nada entra como participante.",
      },
      {
        title: "Cadena de IA en nube procesa el audio",
        body: "Deepgram transcribe el audio (OpenAI Whisper y Groq como respaldo automático). OpenAI redacta resúmenes y respuestas del copiloto (Anthropic Claude Haiku y Groq como respaldo). Modal ejecuta pyannote para detectar quién habla. Los payloads son estrechos y revisados en código.",
      },
      {
        title: "Tú guardas el registro útil",
        body: "La transcripción, etiquetas de hablantes, resumen y respuestas del copiloto se quedan localmente en la app de escritorio — listos para revisar, exportar o borrar.",
      },
    ],
    caveat:
      "El audio se captura localmente, se procesa por una cadena multi-proveedor en nube revisada y vuelve guardado a la app de escritorio.",
  },
  leadMagnet: {
    badge: "Lead magnet",
    heading: "Recibe los 5 trucos prácticos para rendir mejor en calls técnicas en inglés",
    description:
      "Un playbook corto y directo para ingenieros, consultores y sales engineers ESL: qué preparar antes de la reunión, cómo destrabarte cuando te quedas en blanco y cómo responder con claridad bajo presión.",
    emailLabel: "Correo de trabajo",
    emailPlaceholder: "tu@empresa.com",
    cta: "Quiero la guía",
    ctaLoading: "Enviando...",
    privacyNote: "Usamos tu correo solo para esta guía y actualizaciones de Minuta. Puedes darte de baja cuando quieras.",
    success: "Guía solicitada. Revisa tu bandeja en los próximos minutos.",
    error: "No pudimos enviar tu correo ahora. Inténtalo de nuevo en unos minutos.",
    invalidEmail: "Ingresa un correo válido.",
  },
  showcase: {
    slides: [
      {
        tag: "Sin bot en la call",
        heading: "Minuta queda en tu pantalla, no en la lista de participantes",
        body: "Úsalo con Google Meet, Zoom o Teams sin agregar un notetaker a la reunión. La app escucha desde el desktop y queda disponible mientras la conversación avanza.",
        alt: "Google Meet abierto con el control mini de Minuta escuchando la reunión sin agregar un bot a la llamada.",
      },
      {
        tag: "Copiloto en vivo",
        heading: "Aparece la pregunta. También la respuesta.",
        body: "Cuando alguien hace una pregunta difícil en inglés, portugués o español, Minuta detecta el momento y sugiere una respuesta clara para leer, ajustar o decir en el instante.",
        alt: "Panel en vivo de Minuta sobre una llamada de Google Meet, con una pregunta detectada y una respuesta sugerida por el copiloto.",
      },
      {
        tag: "Antes de la reunión",
        heading: "Prepara idioma, contexto y captura antes de la call",
        body: "Abre Minuta antes de la reunión, elige el idioma, activa traducción si la necesitas y agrega contexto para que el copiloto responda con más precisión.",
        alt: "Pantalla inicial de Minuta para configurar sesión, idioma, traducción, micrófono y contexto antes de empezar a escuchar.",
      },
    ],
  },
  problem: {
    heading: "Tú sabes de esto. Solo que no siempre se nota en el momento.",
    body1:
      "Estás en una call con un cliente. Te preguntan algo específico sobre un stack que has entregado decenas de veces — y el nombre del servicio no te viene. O estás explicando una arquitectura y sabes que hay una forma más clara de decirlo, pero bajo presión sale confuso.",
    body2:
      "No es falta de conocimiento. Es la fricción entre saber y actuar en tiempo real.",
  },
  englishBarrier: {
    label: "Reuniones multilingües",
    heading: "Entiendes el tema. Ahora responde en el idioma de la sala.",
    body1:
      "Tu criterio técnico ya está ahí. El momento difícil es en vivo: alguien pregunta en inglés, portugués o español, la sala espera, y necesitas responder con claridad antes de que la conversación avance.",
    body2:
      "Minuta cierra esa brecha con transcripción en tiempo real, traducción opcional entre inglés, portugués y español, y una sugerencia de respuesta que puedes leer y decir con confianza en el idioma correcto.",
    bullets: [
      "Traducción en vivo entre inglés, portugués y español mientras el otro lado habla",
      "¿No entendiste una frase? La transcripción está ahí — retrocede sin romper la call",
      "El copiloto arma la respuesta en el idioma de la reunión, a tu nivel técnico",
      "Funciona en Zoom, Teams, Google Meet — sin bots en la call, nadie lo ve",
    ],
  },
  useCases: {
    label: "Casos de uso",
    heading: "Llega la pregunta. La respuesta ya empieza a formarse.",
    subheading:
      "Momentos reales de call técnica en inglés, portugués y español — y lo que el copiloto sugiere que respondas.",
    saidLabel: "Preguntaron:",
    sayLabel: "Di esto:",
    items: [
      {
        tab: "Call técnica",
        scenario: "En medio de la call, el cliente hace una pregunta puntual de arquitectura.",
        said: "How would you handle retries and idempotency if that payment webhook fires twice?",
        say: "I'd make the handler idempotent — key each event by its provider ID and skip anything already processed. Retries become safe by design: a double-fire just hits the dedup check. I set this up on a billing integration last year.",
      },
      {
        tab: "Demo / objeción",
        scenario: "Durante la demo, el cliente compara el producto con un competidor.",
        said: "This looks fine, but Competitor X already does it and we're not paying extra for that.",
        say: "Fair — the overlap is real. The difference is where it runs: ours keeps the data on your side and never adds a bot to the call. If that's not a constraint for you, X is reasonable; if it is, that's the line.",
      },
      {
        tab: "Sync con el equipo",
        scenario: "Sync con el equipo internacional. Piden el estado y respondes en vivo.",
        said: "Where are we on the migration — is it still on track for this sprint?",
        say: "The migration is on track. Schema changes are merged and tested; what's left is the backfill, which runs tonight. If it comes back clean, we close this tomorrow — no blockers right now.",
      },
    ],
  },
  features: {
    label: "Bajo el capó",
    heading: "Liviano, silencioso y rápido.",
    items: [
      {
        title: "Procesamiento rápido en nube",
        description:
          "La captura de audio queda local, pero la transcripción, ID de hablantes y copiloto corren por APIs seguras con retención limitada. Sin modelos gigantescos pesando en tu computadora.",
        tag: "Velocidad",
      },
      {
        title: "Transcribe, traduce e identifica hablantes",
        description:
          "Detecta voz en tiempo real, transcribe automáticamente en inglés, portugués y español, y traduce cuando necesitas un segundo idioma en pantalla. Al terminar, identifica quién habló en cada segmento — sin pasos manuales.",
        tag: "Pipeline",
      },
      {
        title: "Acta automática con resumen y tareas",
        description:
          "Al detener la grabación, la IA genera un acta completa: resumen ejecutivo, puntos clave y action items identificados. Listo para pegar en Notion, Obsidian o correo.",
        tag: "Salida",
      },
    ],
  },
  copilot: {
    badge: "Exclusivo de Pro",
    heading: "Tu copiloto dentro de la reunión",
    description:
      "Esta es la función principal de Minuta. El copiloto escucha la reunión y detecta automáticamente cuando te hacen una pregunta. Con un clic, recibe una sugerencia de respuesta basada en tus documentos, lo que ya se dijo en la reunión y búsquedas en tiempo real en la web. Disponible solo en el plan Pro.",
    bullets: [
      "Detecta preguntas automáticamente en la transcripción en vivo",
      "Respuestas basadas en tus documentos + transcripción de la reunión",
      "Búsqueda web en tiempo real para enriquecer la respuesta (opcional)",
      "Carga PDFs, notas o textos como contexto antes de empezar",
    ],
    mockup: {
      barLabel: "Copiloto",
      liveLabel: "Transcripción en vivo",
      questionBadge: "Pregunta detectada",
      ctaButton: "Qué responder",
      responseBadge: "Sugerencia de respuesta",
      addFile: "Agregar archivo",
      sourcesLabel: "Fuentes:",
      transcript: [
        { time: "14:21", speaker: "María", text: "Revisemos los próximos pasos de la propuesta." },
        { time: "14:38", speaker: "Carlos", text: "¿El plazo que mencionaste antes sigue en pie?" },
      ],
      detectedQ: {
        time: "14:52",
        speaker: "Juan",
        text: "¿Cuáles son los diferenciales del producto frente al competidor X?",
      },
      responseText:
        "Basándote en la propuesta cargada y lo discutido, puedes destacar: integración nativa con herramientas existentes, soporte dedicado incluido en todos los planes y el descuento por volumen ya mencionado que aplica aquí.",
      chatSources: ["propuesta.pdf", "transcripción", "búsqueda web"],
    },
  },
  differentiation: {
    label: "Por qué Minuta",
    heading: "Esto no es otro notetaker con IA.",
    items: [
      {
        tag: "Contexto profundo",
        title: "Tu perfil, no una lectura genérica del currículum",
        description:
          "Otros copilotos te dan respuestas de Wikipedia. Minuta habla desde lo que tú sabes, lo que has construido y cómo realmente explicas las cosas.",
      },
      {
        tag: "Invisible",
        title: "Sin bots, sin interrupción",
        description:
          "Nada se une a tu llamada. El otro lado no ve nada. Es solo para ti, en tu pantalla — incluso al compartir pantalla.",
      },
      {
        tag: "Calls técnicas",
        title: "Hecho para quienes viven en calls técnicas",
        description:
          "Consultores, ingenieros, arquitectos, sales engineers. Si tu trabajo es explicar cosas complejas en vivo, Minuta fue diseñado para ti.",
      },
    ],
  },
  howItWorks: {
    label: "Cómo funciona",
    heading: "Tres pasos. Después solo preséntate a la reunión.",
    steps: [
      {
        n: "01",
        title: "Dile a Minuta quién eres",
        body: "Sube tu currículum, tus proyectos, tu stack y cualquier doc que importe. Cuanto más contexto, más afiladas las respuestas. Todo guardado en tu perfil.",
      },
      {
        n: "02",
        title: "Abre Minuta antes de la call",
        body: "Funciona con Zoom, Google Meet, Teams o cualquier plataforma. Ningún bot se une a la reunión. Invisible al compartir pantalla.",
      },
      {
        n: "03",
        title: "Obtén respuestas a tu nivel",
        body: "Cuando surge una pregunta, Minuta sugiere una respuesta en tu tono, con tu experiencia, en tu área técnica. Mira, adapta, responde.",
      },
    ],
  },
  personas: {
    label: "Para quién es",
    heading: "Quién está usando Minuta.",
    items: [
      {
        title: "Consultores técnicos & freelancers",
        description:
          "Discovery calls, revisiones de arquitectura, code walkthroughs. Minuta trae el contexto de tus proyectos pasados al momento correcto.",
      },
      {
        title: "Tech leads & engineering managers",
        description:
          "1:1s, reviews de desempeño, reuniones de planificación. Menos \"luego te aviso\", más respuestas directas.",
      },
      {
        title: "Sales engineers",
        description:
          "Demos técnicas en las que una pregunta puede matar un deal. Minuta mantiene tus battlecards al alcance.",
      },
    ],
  },
  privacy: {
    label: "Privacidad",
    heading: "Lo que se dice en tu llamada se queda contigo.",
    bullets: [
      "La captura de audio queda localmente en tu computadora",
      "Tus datos nunca se usan para entrenar modelos",
      "Elimina todo con un clic",
      "Procesamiento seguro en la nube con retención limitada",
    ],
  },
  pricing: {
    label: "Planes",
    heading: "Toda cuenta empieza con 7 días de acceso completo.",
    description:
      "Los registros nuevos desbloquean todo — copiloto, acta, identificación de hablantes — durante los primeros 7 días. Después, Gratis incluye 1 hora/mes de transcripción y traducción. Pro desbloquea grabación ilimitada, copiloto, actas, identificación de hablantes y contexto personal.",
    free: {
      label: "Gratis",
      period: "1 hora / mes",
      tagline: "7 días con acceso completo. Después, 1 hora/mes de transcripción y traducción.",
      features: [
        "7 días con todo desbloqueado — copiloto, acta, identificación de hablantes",
        "1 hora/mes de transcripción y traducción después de la prueba",
        "Grabaciones y transcripciones quedan en tu computadora",
        "Exportar a Markdown / Obsidian",
        "Sin tarjeta de crédito",
      ],
      cta: "Empezar la prueba de 7 días",
    },
    pro: {
      label: "Pro",
      period: "/ mes",
      tagline: "Mantén el copiloto, la acta y la identificación de hablantes después de la prueba. Cancela cuando quieras.",
      badge: "Recomendado",
      features: [
        "Grabación y transcripción ilimitadas",
        "Copiloto de IA dentro de la reunión (exclusivo Pro)",
        "Acta automática con IA — TL;DR, puntos clave, action items",
        "Identificación automática de hablantes en cada grabación",
        "Contexto personal: carga PDFs, notas y documentos",
        "Búsqueda web en tiempo real para enriquecer respuestas",
        "Soporte prioritario",
      ],
      cta: "Suscribirse a Pro",
      ctaManage: "Gestionar suscripción",
      ctaLoading: "Abriendo Stripe…",
      note: "Facturación segura vía Stripe. Cancela cuando quieras.",
      error: "Algo falló al abrir Stripe. Intenta de nuevo en un momento.",
      cycleMonthly: "Mensual",
      cycleAnnual: "Anual",
      annualBilledAs: (total: string, pct: number) =>
        `Facturado anualmente ${total} — ahorra ${pct}%`,
      savingsBadge: (pct: number) => `−${pct}%`,
    },
    footer:
      "Los precios en USD y EUR son indicativos; Stripe cobra en BRL. Tu banco convierte al cambio del día. Cancela cuando quieras, sin preguntas.",
  },
  faq: {
    label: "FAQ",
    heading: "Preguntas frecuentes.",
    items: [
      {
        q: "¿Qué pasa después de los 7 días de prueba?",
        a: "Gratis sigue funcionando con 1 hora/mes de transcripción y traducción. El copiloto en la reunión, el acta automática, la identificación de hablantes, el contexto personal y la grabación ilimitada pasan a ser exclusivos de Pro — suscríbete cuando quieras para recuperarlos. No hay cargos automáticos; nunca te pedimos la tarjeta al registrarte.",
      },
      {
        q: "¿El otro lado sabe que estoy usando Minuta?",
        a: "No. Minuta no se une a la reunión y es invisible al compartir pantalla.",
      },
      {
        q: "¿Funciona en portugués y español?",
        a: "Sí. Los idiomas en producción son inglés, portugués (pt-BR) y español. Otros idiomas están en beta.",
      },
      {
        q: "Mi reunión cambia entre inglés, portugués y español. ¿Minuta ayuda?",
        a: "Sí. Minuta soporta inglés, portugués (pt-BR) y español para transcripción, traducción, actas y respuestas del copiloto. El copiloto Pro puede armar la respuesta en el idioma que necesitas en ese momento.",
      },
      {
        q: "¿Traduce en vivo durante Zoom, Teams o Google Meet?",
        a: "Sí. Minuta captura el audio del sistema de cualquier app de reunión, transcribe inglés, portugués y español de forma segura en la nube, y traduce entre los idiomas soportados en tiempo real. Sin plugin, sin bots.",
      },
      {
        q: "¿Necesito instalar algo en Zoom, Meet o Teams?",
        a: "No. Minuta corre en tu computadora y escucha el audio del sistema — sin plugins, sin bots.",
      },
      {
        q: "¿Mis reuniones quedan grabadas en algún lugar?",
        a: "Solo si tú quieres. Por defecto solo la transcripción se guarda localmente en tu dispositivo.",
      },
      {
        q: "¿Hay versión para Windows o Linux?",
        a: "Mac e Windows primero. Linux está en el roadmap — sin fecha comprometida aún.",
      },
    ],
  },
  footerCta: {
    heading: "¿Listo para responder con claridad en tu próxima reunión?",
    body: "Descarga Minuta y úsalo en tu próxima reunión. Gratis por 7 días, sin tarjeta.",
    cta: "Descargar Minuta",
    ctaSub: "macOS · Windows · Linux pronto",
  },
  footer: {
    tagline: "zero-retention",
    support: "Soporte",
    privacy: "Privacidad",
    terms: "Términos",
    compareHeading: "Comparaciones",
    useCasesHeading: "Casos de uso",
    resourcesHeading: "Recursos",
  },
  relatedLinks: {
    comparisonsHeading: "Otras comparaciones honestas",
    comparisonsLede: "Elige la herramienta de reunión que encaja con tu call.",
    useCasesHeading: "Otras situaciones para las que Minuta es ideal",
    useCasesLede: "Más escenarios donde profesionales se mantienen afilados en calls en vivo en inglés, portugués y español.",
    keepExploring: "Sigue explorando",
  },
  userMenu: {
    account: "Mi cuenta",
    signOut: "Cerrar sesión",
  },
  account: {
    heading: "Mi cuenta",
    youAreOn: "Estás en el plan",
    plan: "",
    currentBadge: "Plan actual",
    freeNote: "Gratis incluye 1 hora/mes de transcripción y traducción. Suscríbete cuando quieras grabación ilimitada y copiloto.",
    proNote: "Gracias por apoyar Minuta. El copiloto dentro de la reunión está desbloqueado.",
    manageSubscription: "Gestionar suscripción",
    openingPortal: "Abriendo Stripe…",
    error: "Algo falló. Intenta de nuevo en un momento.",
  },
  auth: {
    shell: {
      homeLabel: "Volver a la página principal",
      step: (current, total, label) => `Paso ${current}/${total} · ${label}`,
      footer:
        "Al registrarte aceptas los términos y la política de privacidad. Solo tu correo se guarda para gestionar tu cuenta.",
    },
    login: {
      heading: "Iniciar sesión",
      subheading: "Accede a tu cuenta Minuta.",
      emailLabel: "Correo electrónico",
      passwordLabel: "Contraseña",
      submitting: "Iniciando sesión…",
      submit: "Iniciar sesión",
      noAccount: "¿No tienes cuenta?",
      createFree: "Crear cuenta gratis",
      errorEmail: "Ingresa un correo válido.",
      errorPassword: "Ingresa tu contraseña.",
      continueGoogle: "Continuar con Google",
      orDivider: "o",
      callbackBusy: "Finalizando inicio de sesión…",
      callbackError: "No pudimos completar el inicio de sesión. Inténtalo de nuevo desde la página principal.",
    },
    signup: {
      headingFree: "Crear cuenta gratis",
      headingPro: "Crear cuenta Pro",
      subFree: "Sin tarjeta. Sin cargos. Descarga la app y empieza.",
      subPro: "Primero crearemos tu acceso. La facturación por Stripe llegará pronto.",
      firstNameLabel: "Nombre",
      lastNameLabel: "Apellido",
      emailLabel: "Correo electrónico",
      passwordLabel: "Contraseña",
      passwordHint: "Al menos 8 caracteres.",
      passwordWarn: "Mínimo 8 caracteres.",
      submitting: "Creando cuenta…",
      submit: "Crear cuenta",
      codeNote: "Te enviaremos un código de 6 dígitos a tu correo.",
      stepLabel: "crear cuenta",
      errorEmail: "Ingresa un correo válido.",
      errorPassword: "La contraseña debe tener al menos 8 caracteres.",
    },
    confirm: {
      heading: "Revisa tu correo",
      subPre: "Enviamos un código de 6 dígitos a",
      subPost: ".",
      yourEmail: "tu correo",
      codeLabel: "Código de confirmación",
      submitting: "Confirmando…",
      submit: "Confirmar correo",
      changeEmail: "← Cambiar correo",
      codeExpiry: "El código expira en 24h.",
      stepLabel: "confirmar correo",
      errorCode: "El código tiene 6 dígitos.",
      errorEmail: "Correo faltante. Vuelve al registro y empieza de nuevo.",
    },
    done: {
      heading: "Cuenta confirmada",
      subFree:
        "Todo listo. Descarga Minuta para Mac e inicia sesión con el correo y contraseña que acabas de crear.",
      subPro:
        "Tu cuenta Pro está creada. La facturación vía Stripe se activará pronto — mientras tanto, estás en la lista de acceso anticipado.",
      downloadCta: "Descargar Minuta para macOS",
      homeCta: "Volver al inicio",
      proNextStepsLabel: "Próximos pasos — Pro",
      proNextSteps: [
        "Por ahora, la app funciona igual que la cuenta Gratis mientras integramos Stripe.",
        "Recibirás un correo cuando la facturación esté activa — solo autorízala.",
        "¿Preguntas o errores? Contáctanos en soporte.",
      ],
      troubleNote:
        "¿Problemas para iniciar sesión? Intenta restablecer tu contraseña en la app — la pantalla de inicio de sesión tiene el enlace.",
    },
  },
  download: {
    eyebrow: "App de escritorio privada",
    heading: "Descargar Minuta",
    autoArm64:
      "Parece que estás en Apple Silicon. Descarga la versión macOS destacada y abre Minuta antes de tu próxima reunión.",
    autoX64:
      "Parece que estás en un Mac Intel. Descarga la versión macOS destacada y abre Minuta antes de tu próxima reunión.",
    autoWindows:
      "Parece que estás en Windows. El instalador recomendado está destacado abajo.",
    autoWindowsArm64:
      "Parece que estás en Windows on ARM. El instalador ARM64 está destacado abajo.",
    autoUnknown:
      "Elige la versión que coincide con tu computadora. No necesitas cuenta para descargar.",
    supportedKicker: "Paso 1",
    supportedHeading: "Elige tu plataforma",
    supportedBody:
      "Minuta tiene builds de escritorio nativos para macOS y Windows. La opción detectada aparece destacada, pero todas siguen disponibles para laptops de trabajo e instalaciones en otro dispositivo.",
    macPickerLabel: "macOS",
    winPickerLabel: "Windows",
    archArm64: "Apple Silicon",
    archArm64Hint: "M1 · M2 · M3 · M4",
    archX64: "Mac Intel",
    archX64Hint: "Macs anteriores a 2021",
    winSetup: "Instalador (recomendado)",
    winSetupHint: "Windows 10/11 · x64 · .exe",
    winPortable: "Portátil",
    winPortableHint: "Sin instalación · x64 · corre desde la carpeta",
    winSetupArm64: "Instalador · ARM64",
    winSetupArm64Hint: "Windows 11 ARM · arm64 · .exe",
    winPortableArm64: "Portátil · ARM64",
    winPortableArm64Hint: "Sin instalación · arm64 · corre desde la carpeta",
    firstRunKicker: "Paso 2",
    firstRunHeading: "Apertura instantánea y liviana",
    firstRunBody:
      "Minuta ejecuta los modelos de transcripción e IA en la nube, sin descargas gigantescas de modelos. El inicio es inmediato y consume el mínimo de recursos.",
    firstRunItems: [
      "App de escritorio súper liviana: menos de 100 MB de espacio.",
      "Inundado de velocidad: transcripción y resúmenes rápidos hospedados en Groq (Whisper/Llama).",
      "Funciona con Zoom, Teams, Google Meet y cualquier app de reunión que reproduzca audio en tu computadora.",
    ],
    securityKicker: "Nota de confianza",
    securityHeading: "Sin bot, pipeline seguro y retención temporaria",
    securityBody:
      "Minuta no mete un bot en tu reunión. La captura de audio queda local en tu computadora, y la transcripción/IA corren por APIs seguras en la nube con retención limitada.",
    securityItems: [
      "Ningún bot entra a la call y nada aparece para los demás participantes.",
      "Retención temporaria: no guardamos tu audio o transcripciones permanentemente y nunca usamos tus datos para entrenar modelos.",
      "La versión para Mac está firmada y notarizada; el instalador para Windows está firmado de fábrica para integración directa.",
    ],
    troubleshootingKicker: "Si el sistema bloquea la app",
    troubleshootingHeading: "Avisos en la primera apertura",
    troubleshootingBody:
      "Estas notas van debajo de la descarga principal porque la mayoría de usuarios solo las necesita después de instalar. Son esperadas con el empaquetado beta actual y no cambian el flujo de descarga.",
    homeCta: "Volver al inicio",
    footnote: "macOS 13+ / Windows 10 21H2+ · el enlace firmado expira en 5 minutos",
    gatekeeperLabel: "Instalar en macOS",
    gatekeeperExplain:
      "Abre el DMG, arrastra Minuta a Aplicaciones y luego ábrela desde Finder. La versión para Mac está firmada y notarizada; no hace falta ejecutar comandos en la Terminal.",
    winSmartScreenLabel: "Si Windows bloquea la instalación",
    winSmartScreenExplain:
      "La beta para Windows todavía no tiene firma de código, así que SmartScreen puede mostrar un aviso de app no reconocida en la primera instalación.",
    winSmartScreenSteps: [
      "Haz clic en Más información en el aviso de SmartScreen.",
      "Confirma que el editor aparece como Editor desconocido.",
      "Haz clic en Ejecutar de todas formas para abrir el instalador.",
    ],
    detectedBadge: "Detectado",
    comingSoonBadge: "Pronto",
    linuxPickerLabel: "Linux",
    linuxComingSoon:
      "La versión para Linux está en el roadmap — debería salir justo después de cerrar el primer ciclo de release de Windows. ¿Quieres recibir un correo cuando salga? Crea una cuenta y te avisamos.",
  },
  billing: {
    done: {
      heading: "Pago completado",
      subProcessing:
        "Estamos activando tu plan Pro. El webhook de Stripe tarda unos segundos — esta página se actualiza sola.",
      subReady:
        "Tu plan Pro está activo. Abre Minuta e inicia sesión de nuevo si la app ya está abierta, para desbloquear el copiloto.",
      openApp: "Abrir Minuta",
      homeCta: "Volver al inicio",
      refreshCta: "Actualizar estado",
      error: "Aún no podemos confirmar el pago. Intenta actualizar en un momento.",
    },
    cancelled: {
      heading: "Compra cancelada",
      sub: "No se realizó ningún cargo. Puedes intentar de nuevo cuando quieras — nada se perdió.",
      retryCta: "Intentar de nuevo",
      homeCta: "Volver al inicio",
    },
    proPurchase: {
      signedOut: "Inicia sesión para completar tu compra del plan Pro.",
      cta: "Completar pago",
      ctaSignIn: "Iniciar sesión para pagar",
      ctaLoading: "Abriendo Stripe…",
      error: "Algo falló al abrir Stripe. Intenta de nuevo en un momento.",
    },
  },
  launch: {
    hero: {
      eyebrow: "En vivo en Product Hunt · 14/05/2026",
      h1: "Deja de bloquearte cuando llega la pregunta.",
      sub: "Un copiloto multilingüe para tu reunión. Rápido en la nube. Sin bots.",
      cta: "Empezar gratis",
      ctaSub: "7 días con todo · sin tarjeta",
      ctaSecondary: "Leer la nota del fundador",
    },
    tldr: {
      heading: "Minuta en 30 segundos",
      body:
        "Minuta es una app para Mac/Windows que convierte cualquier llamada de Zoom, Teams o Google Meet en un copiloto en tiempo real para reuniones en inglés, portugués y español. Captura el audio localmente, lo procesa a través de APIs de alta velocidad en la nube y sugiere respuestas en el idioma que necesitas, a tu nivel técnico, basadas en PDFs y anotaciones cargadas. Sin bot en la llamada, invisible para el otro lado. Instalación liviana de 100 MB. Diseñado para profesionales que necesitan responder con claridad mientras la reunión sigue ocurriendo.",
    },
    video: {
      heading: "60 segundos, de principio a fin",
      sub: "Una call real. La sidebar de Minuta. Sin cortes.",
      embedUrl: "",
      caption: "Si el embed no carga, míralo en Loom →",
    },
    founderNote: {
      heading: "Soy Mark. Hoy es el día del lanzamiento — AMA.",
      body:
        "Construí Minuta porque soy ingeniero ESL y perdí clientes en calls técnicas — no porque no supiera la respuesta, sino porque no lograba armarla en inglés a tiempo. Estoy en solo. Voy a responder en Product Hunt y por correo todo el día. Cuéntame qué falta — leo todo.",
      signature: "— Mark",
    },
    discount: {
      eyebrow: "Solo hoy en Product Hunt · termina 21/05/2026",
      headline: "Combina HUNT30 con el plan anual",
      price: "€67,20 / año",
      priceWas: "en lugar de €108",
      sub: "El anual ya ahorra 20%. HUNT30 suma otros 30% el primer año. O úsalo en el mensual: €6,30/mes, fijo de por vida.",
      code: "HUNT30",
      codeLabel: "Código",
      button: "Reclamar la oferta del lanzamiento",
      buttonSub: "Aplica HUNT30 en el checkout",
    },
    faq: {
      heading: "Preguntas comunes sobre el lanzamiento",
      items: [
        {
          q: "¿Minuta es open source?",
          a: "Aún no. La app de escritorio es closed-source hoy, pero el trabajo pesado corre en modelos abiertos que puedes auditar: Whisper para transcripción, Llama / Qwen para el copiloto, pyannote para diarización. El roadmap contempla abrir partes del pipeline cuando la API se estabilice.",
        },
        {
          q: "¿Cómo se garantiza la privacidad en la práctica?",
          a: "El audio se captura localmente en tu computadora y se procesa de forma segura a través de APIs de alta velocidad en la nube (Groq Whisper y Llama). No almacenamos tu audio o transcripciones permanentemente y nunca usamos tus datos para entrenar modelos. Solo tu correo electrónico es guardado en nuestros servidores.",
        },
        {
          q: "¿Por qué no usar Otter o Granola?",
          a: "Otter y Granola son buenos notetakers en la nube para equipos que principalmente quieren un transcript. Minuta es para el momento en que tienes que responder en una conversación técnica en vivo — en inglés, portugués o español — mientras el otro lado sigue esperando. Problema distinto. Mantenemos una comparación honesta en el sitio.",
        },
        {
          q: "¿Qué hay en el roadmap?",
          a: "Roadmap público: build para Linux (Q3 2026), coaching in-app (feedback post-call de tus respuestas), plan equipo con perfil compartido y CLI para transcripción headless. Lo demás que valga la pena, lo votas en el roadmap público. Entregamos lo que pide el usuario, en orden.",
        },
        {
          q: "¿Cómo funcionan el free y el trial Pro de verdad?",
          a: "Regístrate — 7 días con todo desbloqueado, copiloto incluido. Sin tarjeta. Después del día 7, Gratis incluye 1 hora/mes de transcripción y traducción. El copiloto en reunión, acta, identificación de hablantes, contexto personal y grabación ilimitada pasan a Pro (€9/mes, o €7,50/mes en el anual). Cancela cuando quieras en Stripe.",
        },
        {
          q: "¿Qué tan rápido es la instalación?",
          a: "La instalación es instantánea. Dado que procesamos la transcripción e IA en la nube, el instalador del escritorio pesa menos de 100 MB. Puedes descargar, registrarte y empezar tu primera grabación en menos de un minuto, sin descargas masivas.",
        },
      ],
    },
    banner: {
      text: "En vivo en Product Hunt · 30% off en Pro de por vida con HUNT30",
      cta: "Ver el lanzamiento →",
      dismissAriaLabel: "Cerrar banner del lanzamiento",
    },
  },
  teamsWaitlist: {
    seoTitle: "Minuta para equipos — únete a la waitlist",
    seoDescription:
      "Perfil compartido, biblioteca de docs del equipo y billing centralizado. Cuéntanos sobre tu equipo y Mark te contacta personalmente cuando el plan equipo se lance.",
    eyebrow: "Plan equipo · acceso anticipado",
    heading: "Un copiloto de reuniones para todo el equipo",
    subheading:
      "Hoy Minuta es un plan Pro por asiento. El plan equipo añade perfil compartido (la forma en que tu equipo se describe), biblioteca compartida de docs, billing centralizado y uso por asiento. Deja tu correo de trabajo y una línea sobre tu equipo — Mark te contacta personalmente cuando se lance.",
    nameLabel: "Tu nombre",
    namePlaceholder: "Mark Gerald",
    emailLabel: "Correo de trabajo",
    emailPlaceholder: "tu@empresa.com",
    teamSizeLabel: "¿Cuántas personas en el equipo?",
    teamSizeOptions: [
      { value: "2-5", label: "2–5" },
      { value: "6-15", label: "6–15" },
      { value: "16-50", label: "16–50" },
      { value: "50+", label: "50+" },
    ],
    notesLabel: "¿Qué hace tu equipo? (opcional)",
    notesPlaceholder: "Sales engineering en una SaaS B2B, mayoría clientes US…",
    cta: "Apúntame en la waitlist",
    ctaLoading: "Enviando…",
    privacyNote:
      "Solo lo usamos para avisarte cuando el plan equipo se lance y hacer 1–2 preguntas de follow-up. Sin lista de marketing, sin reventa.",
    success: "Estás en la lista. Mark te enviará un correo personal antes del lanzamiento del plan equipo.",
    error: "No pudimos enviar el formulario ahora. Inténtalo de nuevo en unos minutos.",
    invalidEmail: "Ingresa un correo de trabajo válido.",
    afterSubmit: {
      heading: "Gracias — estás en la lista.",
      body: "Mark lee cada entrada. Espera un correo personal cuando el plan equipo esté listo (y posiblemente una o dos preguntas rápidas antes).",
      ctaHome: "Volver a Minuta",
      ctaPricing: "Ver el plan Pro por asiento",
    },
  },
};
